import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Mail, CheckCircle2, Trash2 } from "lucide-react";
import { StatusPill, formatDate } from "@/components/admin/AdminShared";

export const EmailAccountsList = ({ compact = false, refreshKey = 0 }) => {
  const [accounts, setAccounts] = useState(null);

  const load = () => api.get("/admin/email-accounts").then((r) => setAccounts(r.data.accounts));
  useEffect(() => {
    load();
  }, [refreshKey]);

  const run = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const markActive = (a) => run(() => api.put(`/admin/email-accounts/${a.id}`, { status: "Active" }), `${a.address} marked as active`);

  const remove = async (a) => {
    if (!window.confirm(`Remove ${a.address} from this list?`)) return;
    run(() => api.delete(`/admin/email-accounts/${a.id}`), "Email account removed");
  };

  if (!accounts) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div data-testid="email-accounts-list" className="bg-white rounded-2xl border border-purple-100 divide-y divide-purple-50">
      {accounts.map((a) => (
        <div key={a.id} data-testid={`email-account-${a.address}`} className="flex flex-wrap items-center gap-3 px-5 py-4">
          <div className="w-9 h-9 rounded-full bg-purple-50 flex items-center justify-center">
            <Mail className="w-4 h-4 text-purple-600" />
          </div>
          <div className="flex-1 min-w-[180px]">
            <p className="font-semibold text-purple-950 text-sm">{a.address}</p>
            <p className="text-xs text-muted-foreground">
              {a.name}
              {!compact && ` · ${a.requested_by} · ${formatDate(a.created_at)}`}
              {!compact && a.notes && ` · ${a.notes}`}
            </p>
          </div>
          <StatusPill tone={a.status === "Active" ? "green" : "amber"} testId={`email-status-${a.address}`}>
            {a.status === "Active" ? "Active" : "Pending · 8–12 hrs"}
          </StatusPill>
          {!compact && (
            <div className="flex gap-1">
              {a.status !== "Active" && (
                <button data-testid={`email-mark-active-${a.address}`} onClick={() => markActive(a)} title="Mark as active" className="p-2 rounded-lg hover:bg-emerald-50 text-emerald-600">
                  <CheckCircle2 className="w-4 h-4" />
                </button>
              )}
              <button data-testid={`email-delete-${a.address}`} onClick={() => remove(a)} title="Remove" className="p-2 rounded-lg hover:bg-red-50 text-red-500">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
